import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { each, isEmpty } from 'lodash';
import { concatMap, forkJoin, from, of } from 'rxjs';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { State } from 'src/app/interfaces/state';
import { AddressService } from 'src/app/services/address.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';
import { vendorsService } from 'src/app/shared/vive-api/vendors.service';
import { AddressDto, VendorDto } from 'src/app/shared/vive-api/vive-api-models';

@Component({
  selector: 'app-edit-vendor-address-modal',
  templateUrl: './edit-vendor-address-modal.component.html',
  styleUrl: './edit-vendor-address-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EditVendorAddressModalComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ToastService: ToastService,
    private VendorService: VendorService,
    private UtilityService: UtilityService,
    private ActiveModal: NgbActiveModal,
    private AddressService: AddressService
  ) {}

  @Input() VendorId!: number;

  streetAddress?: AddressDto;
  mailingAddress?: AddressDto;
  remitToAddress?: AddressDto;
  country!: 'US' | 'CA';
  localizationInfo!: LocalizationInfo;

  streetAddressForm: FormGroup = new FormGroup({
    id: new FormControl(null),
    VendorId: new FormControl(null, [Validators.required]),
    address1: new FormControl(null, [Validators.required]),
    address2: new FormControl(null),
    city: new FormControl(null, [Validators.required]),
    state: new FormControl(null, [Validators.required]),
    zip: new FormControl(null, [Validators.required]),

    isStreet: new FormControl(true),
    isMailing: new FormControl(false),
    isRemitTo: new FormControl(false),

    country: new FormControl(null, [Validators.required])
  });

  mailingAddressForm: FormGroup = new FormGroup({
    id: new FormControl(null),
    VendorId: new FormControl(null, [Validators.required]),
    address1: new FormControl(null, [Validators.required]),
    address2: new FormControl(null),
    city: new FormControl(null, [Validators.required]),
    state: new FormControl(null, [Validators.required]),
    zip: new FormControl(null, [Validators.required]),

    isMailing: new FormControl(true),
    isStreet: new FormControl(false),
    isRemitTo: new FormControl(false),

    country: new FormControl(null, [Validators.required])
  });

  remitToAddressForm: FormGroup = new FormGroup({
    id: new FormControl(null),
    VendorId: new FormControl(null, [Validators.required]),
    address1: new FormControl(null, [Validators.required]),
    address2: new FormControl(null),
    city: new FormControl(null, [Validators.required]),
    state: new FormControl(null, [Validators.required]),
    zip: new FormControl(null, [Validators.required]),

    isRemitTo: new FormControl(true),
    isStreet: new FormControl(false),
    isMailing: new FormControl(false),

    country: new FormControl(null, [Validators.required])
  });

  isValidationErrors() {
    let isErrors = false;

    each(
      this.streetAddressForm.controls,
      (currentControl: AbstractControl, key: string) => {
        if (currentControl.errors) {
          isErrors = true;
          currentControl.markAsDirty();
        }
      }
    );

    // Only perform street address validation if checkbox is selected.
    if (this.mailingAddressForm.controls['isMailing'].value) {
      each(
        this.mailingAddressForm.controls,
        (currentControl: AbstractControl, key: string) => {
          if (currentControl.errors) {
            isErrors = true;
            currentControl.markAsDirty();
          }
        }
      );
    }

    // Only perform remit-to address validation if checkbox is selected.
    if (this.remitToAddressForm.controls['isRemitTo'].value) {
      each(
        this.remitToAddressForm.controls,
        (currentControl: AbstractControl, key: string) => {
          if (currentControl.errors) {
            isErrors = true;
            currentControl.markAsDirty();
          }
        }
      );
    }

    if (isErrors) {
      this.ToastService.addErrorToast(
        'Required fields must be filled in',
        'One or more required fields have not been completed. Please provide the necessary information.',
        { returnError: true, centerHeader: true }
      );
    }

    return isErrors;
  }

  close() {
    this.ActiveModal.dismiss();
  }

  confirm() {
    if (this.isValidationErrors()) {
      return;
    }

    const observables = [of({})];

    if (
      !this.mailingAddressForm.controls['isMailing'].value &&
      this.mailingAddress &&
      this.mailingAddress.id !== this.streetAddress?.id
    ) {
      if (!this.mailingAddress.id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Mailing Address is missing id.',
          { returnError: true }
        );
      }
      observables.push(
        this.AddressService.deleteAddress(this.mailingAddress.id)
      );
    }

    if (
      !this.remitToAddressForm.controls['isRemitTo'].value &&
      this.remitToAddress &&
      this.remitToAddress.id !== this.mailingAddress?.id
    ) {
      if (!this.remitToAddress.id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Mailing Address is missing id.',
          { returnError: true }
        );
      }
      observables.push(
        this.AddressService.deleteAddress(this.remitToAddress.id)
      );
    }

    // The addresses save one at a time, each after the previous one resolves.
    forkJoin(observables)
      .pipe(
        concatMap(() =>
          from(
            vendorsService.updateOne({
              pathParams: { vendorId: this.VendorId },
              data: { StreetAddress: this.streetAddressForm.getRawValue() }
            })
          )
        ),
        concatMap(({ StreetAddress }) => {
          this.streetAddress = StreetAddress;

          return this.mailingAddressForm.controls['isMailing'].value
            ? from(
                vendorsService.updateOne({
                  pathParams: { vendorId: this.VendorId },
                  data: {
                    MailingAddress: this.mailingAddressForm.getRawValue()
                  }
                })
              )
            : of<Partial<VendorDto>>({});
        }),
        concatMap(({ MailingAddress }) => {
          if (!isEmpty(MailingAddress)) {
            this.mailingAddress = MailingAddress;
          }

          return this.remitToAddressForm.controls['isRemitTo'].value
            ? from(
                vendorsService.updateOne({
                  pathParams: { vendorId: this.VendorId },
                  data: {
                    RemitToAddress: this.remitToAddressForm.getRawValue()
                  }
                })
              )
            : of<Partial<VendorDto>>({});
        })
      )
      .subscribe({
        next: ({ RemitToAddress }) => {
          if (!isEmpty(RemitToAddress)) {
            this.remitToAddress = RemitToAddress;
          }

          this.ActiveModal.close({
            StreetAddress: this.streetAddress,
            MailingAddress: this.mailingAddress,
            RemitToAddress: this.remitToAddress
          });
        }
      });
  }

  ngOnInit(): void {
    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    this.VendorService.getVendorV2(
      this.VendorId,
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: ['id', 'country'],
        StreetAddress: {},
        MailingAddress: {},
        RemitToAddress: {}
      })
    ).subscribe({
      next: (Vendor) => {
        if (!Vendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Vendor information.',
            { returnError: true }
          );
        }

        const { country, StreetAddress, MailingAddress, RemitToAddress } =
          Vendor;

        if (!country) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing country information.',
            { returnError: true }
          );
        }

        Object.assign(this, { country });

        Object.assign(this, {
          streetAddress: StreetAddress,
          mailingAddress: MailingAddress,
          remitToAddress: RemitToAddress
        });

        if (this.streetAddress) {
          const { address1, address2, city, state, zip } = this.streetAddress;
          this.streetAddressForm.patchValue({
            VendorId: Vendor.id,
            ...(address1 && { address1 }),
            ...(address2 && { address2 }),
            ...(city && { city }),
            ...(state && { state: state as State }),
            ...(zip && { zip }),
            ...(country && { country })
          });
        }

        if (
          this.mailingAddress &&
          this.mailingAddress.id !== this.streetAddress?.id
        ) {
          const { address1, address2, city, state, zip } = this.mailingAddress;
          this.mailingAddressForm.patchValue({
            VendorId: Vendor.id,
            ...(address1 && { address1 }),
            ...(address2 && { address2 }),
            ...(city && { city }),
            ...(state && { state: state as State }),
            ...(zip && { zip }),
            ...(country && { country })
          });
        } else if (this.streetAddress) {
          this.streetAddressForm.patchValue({ isMailing: true });
          this.mailingAddressForm.patchValue({ isMailing: false });
        }

        if (
          this.remitToAddress &&
          this.remitToAddress.id !== this.streetAddress?.id &&
          this.remitToAddress.id !== this.mailingAddress?.id
        ) {
          const { address1, address2, city, state, zip } = this.remitToAddress;
          this.remitToAddressForm.patchValue({
            VendorId: Vendor.id,
            ...(address1 && { address1 }),
            ...(address2 && { address2 }),
            ...(city && { city }),
            ...(state && { state: state as State }),
            ...(zip && { zip }),
            ...(country && { country })
          });
        } else if (this.streetAddress && !this.mailingAddress) {
          this.streetAddressForm.patchValue({ isRemitTo: true });
          this.remitToAddressForm.patchValue({ isRemitTo: false });
        } else if (this.mailingAddress) {
          this.mailingAddressForm.patchValue({ isRemitTo: true });
          this.remitToAddressForm.patchValue({ isRemitTo: false });
        }

        this.localizationInfo = this.UtilityService.getLocalizedValuesMap(
          this.country
        );
        this._cdr.markForCheck();
      }
    });
  }
}
